import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft, Check, Copy, Loader2, Mail, UserPlus } from 'lucide-react';
import { createConsultant } from '../../services/api';

/*
 * Add a Solution Partner: a name and an email. No password is typed here or
 * sent by email. The partner gets one accept link, chooses their own password
 * on it, and lands on a short welcome deck that sets up their intake framework.
 * The link is shown here too, so the admin can hand it over directly.
 */

const ConsultantForm: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ name: '', email: '' });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [sent, setSent] = useState<{ name: string; email: string; url: string | null } | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    const userRole = localStorage.getItem('user_role');
    if (!token || userRole !== 'super') {
      navigate('/login');
    }
  }, [navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      const res = await createConsultant({ name: formData.name.trim(), email: formData.email.trim() });
      setSent({ name: formData.name.trim(), email: formData.email.trim(), url: res.invite_url });
      toast.success(`Invite sent to ${formData.email.trim()}`);
    } catch (error: any) {
      console.error('Error creating consultant:', error);
      toast.error(error.message || 'Failed to invite the Solution Partner');
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      }
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async () => {
    if (!sent?.url) return;
    try {
      await navigator.clipboard.writeText(sent.url);
      setCopied(true);
      toast.success('Invite link copied');
    } catch {
      toast.info('Select the link and copy it yourself');
    }
  };

  const inputClass = (field: string) =>
    `w-full px-3.5 py-2.5 bg-white border rounded-lg text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400 transition-all ${
      errors[field] ? 'border-red-400' : 'border-gray-200'
    }`;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/super/consultants')}
          className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Add Solution Partner</h1>
          <p className="text-sm text-gray-500 mt-0.5">They get a link, choose their own password, and set up their intake</p>
        </div>
      </div>

      <div className="max-w-lg">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-brand-50 flex items-center justify-center">
              {sent ? <Mail size={18} className="text-brand-500" /> : <UserPlus size={18} className="text-brand-500" />}
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-900">{sent ? 'Invite sent' : 'Who are you inviting?'}</h2>
              <p className="text-xs text-gray-500">{sent ? `${sent.name} has 14 days to open the link` : 'Name and work email. Nothing else is needed.'}</p>
            </div>
          </div>

          {sent && (
            <div className="p-6 space-y-5">
              <p className="m-0 text-sm text-gray-600">
                An email is on its way to <span className="font-medium text-gray-900">{sent.email}</span>. When they open the link they choose a password, pick the Agents Anywhere framework or bring their own method, and see the cards their clients will answer.
              </p>
              {sent.url && (
                <div className="flex items-center gap-3 rounded-lg bg-gray-900 px-4 py-3 text-white">
                  <span className="min-w-0 flex-1 truncate text-xs">{sent.url}</span>
                  <button type="button" onClick={copyLink} className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md bg-white px-3 text-xs font-semibold text-gray-900 whitespace-nowrap">
                    {copied ? <Check size={13} /> : <Copy size={13} />}{copied ? 'Copied' : 'Copy link'}
                  </button>
                </div>
              )}
              <p className="m-0 text-xs text-gray-500">The same link is in their email. You can resend a fresh one any time from the Solution Partners list.</p>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setSent(null); setCopied(false); setFormData({ name: '', email: '' }); }}
                  className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Invite another
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/super/consultants')}
                  className="px-5 py-2.5 bg-black text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors"
                >
                  Back to Solution Partners
                </button>
              </div>
            </div>
          )}

          {!sent && (
            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Full Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={inputClass('name')}
                  placeholder="Enter full name"
                />
                {errors.name && <p className="mt-1.5 text-xs text-red-500">{errors.name}</p>}
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={inputClass('email')}
                  placeholder="name@company.com"
                />
                {errors.email && <p className="mt-1.5 text-xs text-red-500">{errors.email}</p>}
              </div>

              {/* The password field is gone on purpose: the partner chooses
                  their own on the accept link, so no credential is typed here
                  or carried by email. The API still accepts one if needed. */}

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => navigate('/super/consultants')}
                  className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-black text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 whitespace-nowrap"
                >
                  {loading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail size={16} />
                      Send invite
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConsultantForm;
